<template>
  <div>
    <h5>{{ i18n.t('/ExchangeCar/BlockTitle') }}</h5>

    <!-- Loop through all exchange cars (max 3) -->
    <div v-for="(car, index) in exchangeCars" :key="index" class="exchange-car-container">
      <!-- Folded/Collapsed View -->
      <div v-if="collapsedStates[index]" class="folded-section">
        <div class="folded-header">
          <div class="folded-info">
            <license-plate-display
              :license-plate="car.LicensePlate"
              :read-only="true"
              class="license-plate-clickable"
              @click="toggleCollapse(index)"
            />
            <span class="folded-car-name">{{ getCarDisplayName(car) }}</span>
          </div>
          <div class="folded-price">
            {{ i18n.n((car.Price?.Incl || 0) * -1, 'currency') }}
          </div>
        </div>
        <app-button
          v-if="!readOnly"
          type="button"
          button-type="secundary"
          data-qa="exchange-car-details"
          :show-icon="false"
          @click="toggleCollapse(index)"
        >
          Bekijk details
        </app-button>
      </div>

      <!-- Expanded View -->
      <div v-else>
        <exchange-car-item
          :exchange-car="car"
          :car-index="index"
          :read-only="readOnly"
          :is-expanded="!collapsedStates[index]"
          @update="handleExchangeCarUpdate"
          @remove="removeExchangeCar(index)"
          @car-fetched="handleCarFetched(index)"
          @toggle-collapse="toggleCollapse(index)"
        />
      </div>
    </div>

    <!-- Total trade-in value -->
    <div v-if="exchangeCars.length > 0 && hasTotalValue" class="total-trade-in">
      <strong>{{ i18n.t('/ExchangeCar/TotalTradeInValue') }}</strong>
      <span class="total-value">{{ i18n.n(totalTradeInValue * -1, 'currency') }}</span>
    </div>

    <!-- Add button (only show if less than 3 cars and last car has details) -->
    <div v-if="!readOnly && canAddNewCar && isCanAddMultipleExchangeCars" class="add-exchange-car">
      <app-button
        type="button"
        button-type="secundary"
        data-qa="addExchangeCar"
        :show-icon="true"
        icon="plus"
        @click="addExchangeCar"
      >
        Voeg inruilauto toe
      </app-button>
    </div>

    <exchange-car-proposition-modal />
  </div>
</template>
<script>
import { useVuelidate } from '@vuelidate/core'
import ExchangeCarItem from '@/components/generic/presentational/organisms/exchangeCarItem/ExchangeCarItem.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import ExchangeCarPropositionModal from '@/components/generic/presentational/organisms/exchangeCarPropositionModal/ExchangeCarPropositionModal.vue'
import LicensePlate from '@/components/generic/presentational/molecules/licensePlate/LicensePlate.vue'
import StoreConnector from '@/helpers/StoreConnector'
import { mapGetters } from 'vuex'

export default {
  components: {
    ExchangeCarItem,
    AppButton,
    ExchangeCarPropositionModal,
    'license-plate-display': LicensePlate,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      exchangeCars: [],
      collapsedStates: {},
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    ...mapGetters({
      exchangeCarType: 'quote/exchangeCarType',
      dealerSettings: 'user/dealerSettings',
    }),
    isCanAddMultipleExchangeCars() {
      return this.dealerSettings.isCanAddMultipleExchangeCars
    },
    canAddNewCar() {
      if (this.exchangeCars.length >= 3) return false
      if (this.exchangeCars.length === 0) return true

      // Check if the last car has been fetched (has ModelName)
      const lastCar = this.exchangeCars[this.exchangeCars.length - 1]
      return lastCar.ModelName != null
    },
    totalTradeInValue() {
      return this.exchangeCars.reduce((sum, car) => {
        return sum + (parseFloat(car.Price?.Incl) || 0)
      }, 0)
    },
    hasTotalValue() {
      return this.exchangeCars.some((car) => {
        const value = parseFloat(car.Price?.Incl)
        return !isNaN(value) && value !== 0
      })
    },
  },
  created() {
    // Subscribe to store changes
    StoreConnector.get('quote/exchangeCars', (data) => {
      this.exchangeCars = data || []

      // Initialize with one empty car if none exist
      if (this.exchangeCars.length === 0) {
        return // Don't process collapsed states yet
      }

      // Initialize collapsed states for new cars ONLY if they don't have a state yet
      this.exchangeCars.forEach((car, index) => {
        if (this.collapsedStates[index] === undefined) {
          // New empty cars (no ModelName) start expanded
          // Cars with data can start collapsed
          this.collapsedStates[index] = true
        }
      })

      // Force reactivity
      this.collapsedStates = { ...this.collapsedStates }
    })
  },
  methods: {
    addExchangeCar() {
      if (this.exchangeCars.length >= 3) {
        return // Max 3 cars
      }

      const currentLength = this.exchangeCars.length
      const newCar = this.createEmptyExchangeCar()

      const updatedCars = [...this.exchangeCars, newCar]

      // Set collapsed states BEFORE dispatching to store
      // Collapse previous car (if exists)
      if (currentLength > 0) {
        this.collapsedStates[currentLength - 1] = true
      }

      // New car starts expanded - use explicit false
      this.collapsedStates[currentLength] = false

      // Force reactivity update
      this.collapsedStates = { ...this.collapsedStates }

      StoreConnector.dispatch('quote/setExchangeCars', updatedCars)
    },

    removeExchangeCar(index) {
      // When there's only one car clear the data instead of removing
      // the slot so the user can re-enter a plate.
      if (this.exchangeCars.length === 1) {
        const updatedCars = [...this.exchangeCars]
        updatedCars[index] = this.createEmptyExchangeCar()
        StoreConnector.dispatch('quote/setExchangeCars', updatedCars)
        // Keep expanded so user can immediately enter a new plate
        this.collapsedStates[index] = false
        this.collapsedStates = { ...this.collapsedStates }
        return
      }

      const updatedCars = this.exchangeCars.filter((_, i) => i !== index)
      StoreConnector.dispatch('quote/setExchangeCars', updatedCars)

      // Remove collapsed state for this index
      delete this.collapsedStates[index]

      // Reindex collapsed states
      const newCollapsedStates = {}
      Object.keys(this.collapsedStates).forEach((key) => {
        const numKey = parseInt(key)
        if (numKey > index) {
          newCollapsedStates[numKey - 1] = this.collapsedStates[key]
        } else if (numKey < index) {
          newCollapsedStates[numKey] = this.collapsedStates[key]
        }
      })
      this.collapsedStates = newCollapsedStates
    },

    handleExchangeCarUpdate({ index, car }) {
      const updatedCars = [...this.exchangeCars]
      updatedCars[index] = car

      // If first car's type changed, update all other cars to match
      // because a exchange car can either only be a VAT / Margin car
      if (index === 0 && updatedCars.length > 1) {
        const newType = car.Type
        for (let i = 1; i < updatedCars.length; i++) {
          if (updatedCars[i].Type !== newType) {
            updatedCars[i] = {
              ...updatedCars[i],
              Type: newType,
              // Reset BPM-related fields when switching types
              ResidualBpm: null,
              Price: {
                ...updatedCars[i].Price,
                Excl: null,
                Btw: null,
                BtwRate: null,
              },
            }
          }
        }
      }

      StoreConnector.dispatch('quote/setExchangeCars', updatedCars)

      // Trigger previewSave to calculate BPM on backend for VAT cars
      if (car.Type === 'VAT' && car.ModelName) {
        StoreConnector.dispatch('quote/previewSave')
      }
    },

    handleCarFetched(index) {
      // Car stays expanded after fetching details
      // This allows the "Voeg inruilauto toe" button to be visible
      // The car will collapse when user adds a new car or clicks "Verberg details"
    },

    toggleCollapse(index) {
      const isExpanding = this.collapsedStates[index]
      this.collapsedStates[index] = !this.collapsedStates[index]
      // Force reactivity
      this.collapsedStates = { ...this.collapsedStates }

      if (isExpanding) {
        this.$nextTick(() => {
          const el = document.querySelector(`#license-plate-input-${index}`)
          const input = el?.tagName === 'INPUT' ? el : el?.querySelector('input')
          if (input) input.focus()
        })
      }
    },

    getCarDisplayName(car) {
      if (!car.BrandName && !car.ModelName) return ''
      return `${car.BrandName || ''} ${car.ModelName || ''}`.trim()
    },

    createEmptyExchangeCar() {
      return {
        Type: this.exchangeCarType,
        LicensePlate: null,
        Vin: null,
        BrandName: null,
        ModelName: null,
        Mileage: null,
        ExchangeDate: null,
        ResidualBpm: null,
        Price: {
          Incl: null,
          Excl: null,
          Btw: null,
          BtwRate: null,
        },
        AdmissionDate: null,
        Bpm: {
          Value: null,
        },
        TradeValue: null,
        Remark: null,
        CommercialVehicle: null,
        CategoryCode: null,
        VehicleType: '',
        Trimline: null,
        InteriorColor: null,
        ExteriorColor: null,
        Engine: null,
        MotExpiryDate: null,
        Licenseplate1AIssueing: null,
        Licenseplate1BRegistration: null,
        FirstRegistrationNL: null,
        FirstRegistrationWorld: null,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.exchange-car-container {
  border: 1px solid $gray-lighter;
  border-radius: 0.5em;
  padding: 1.5em;
  margin-bottom: 1.5em;
  background-color: $white;
}

.folded-section {
  .folded-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1em;

    .folded-info {
      display: flex;
      align-items: center;
      gap: 1em;

      .folded-car-name {
        font-weight: 500;
      }

      // Make license plate component scale nicely in folded view
      :deep(.license-plate-box) {
        transform: scale(0.9);
        transform-origin: left center;
      }

      .license-plate-clickable {
        cursor: pointer;
      }
    }

    .folded-price {
      font-weight: 600;
      font-size: 1.1em;
    }
  }
}

.total-trade-in {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1em 1.5em;
  margin-top: 1em;
  border-top: 2px solid $gray-base;

  strong {
    font-size: 1.1em;
  }

  .total-value {
    font-size: 1.2em;
    font-weight: 600;
  }
}

.add-exchange-car {
  margin-top: 1em;
}
</style>
